/**
 * Precedent: APR's Knowledge Library · Architecture & Process Diagrams —
 * `apps/portal/web/src/pages/apr/settings/KnowledgeLibrary.tsx`, mounted with
 * `category="diagrams"`. CLONED, NOT IMPORTED (D-003): another product's folder.
 *
 * D-025, the owner: *"bring the knowledge Library screen I mention"*, and — asked which documents
 * it should hold — **the same documents as APR**. So this screen and APR's show ONE library: the
 * rows are APR's `agent_knowledge_files` in the `diagrams` category, read and written through
 * this module's own routes (`server/src/erp_consolidation/knowledge/routes.ts`), and anything
 * uploaded or deleted on either screen appears on both. Scoring Definition's Supporting documents
 * (D-024) attach from it.
 *
 * WHAT IS THE SOURCE'S, UNCHANGED: the help text, the title and subtitle, the upload rules (the
 * eight extensions, multiple files, drag-and-drop), Scope / Description / Tags, the filter bar
 * (filename search, scope, tag, Clear), the parent-inheritance rule in the scope filter, the
 * seven columns in their order, the four row actions with their icons and titles, the empty and
 * no-match states, and the preview's renderers (Word via docx-preview, Excel via SheetJS, PDF via
 * the browser, everything else as the extracted text the AI reads).
 *
 * WHAT IS THIS MODULE'S, each for a stated reason:
 *  1. CHROME — `ErpScreen`, `PageHeader`, `HelpPanel` and `recordList.css`'s `erp-rl-*`, which IS
 *     this module's line-cited clone of this very screen (D-018). APR's `rd-*` rules are scoped to
 *     `.apr-app` and do not reach here.
 *  2. PANELS, NOT MODALS — D-017: *"in general please replace all windows from this app by 'right
 *     panel'"*. Upload and Edit open the single-width `FormPanel`, the preview the double width,
 *     and Delete the module's `ConfirmDialog`.
 *  3. THE HEIGHT LOCK — `useScreenLock`, so the header and filters never scroll and only the table
 *     does (CLAUDE.md, "a screen header never scrolls"). APR scrolls its whole page.
 *  4. WRITES BY ROLE — Upload, Edit and Delete appear for a Lead Architect or Administrator
 *     (`canEditCapabilityModel`), the people who build the model the documents support; the server
 *     refuses the rest. APR shows them to every APR user.
 *  5. `alert()` ON A FAILED DOWNLOAD becomes the module's toast.
 */
import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { ConfirmDialog } from '@platform/ui-kit';
import { ErpScreen } from '../ErpScreen';
import { PageHeader } from '../chrome/PageHeader';
import { HelpPanel } from '../chrome/HelpPanel';
import { FormPanel } from '../chrome/FormPanel';
import { type Help } from '../chrome/helpTypes';
import { useScreenLock } from '../chrome/useScreenLock';
import { erpApi } from '../api';
import { canEditCapabilityModel } from '../roles';
import {
  type ErpContext,
  type KnowledgeApplication,
  type KnowledgeFile,
  type KnowledgeFileDetail,
} from '../types';
import '../chrome/recordList.css';
import './KnowledgeLibrary.css';

/* The source's help, verbatim, with the one sentence that names this module's link to it. */
const KnowledgeLibraryHelp: Help = {
  title: 'Knowledge Library',
  icon: '📚',
  purpose:
    'A shared library of source documents — contracts, usage reports, vendor proposals, interview notes — that the strategic analysis agents read as context when producing recommendations. It is the same library as APR&rsquo;s Knowledge Library &rarr; Architecture &amp; Process Diagrams: a document added or deleted here appears there too.',
  doHere: [
    'Click <b>Upload Files</b> to add documents; drag-and-drop or browse (PDF, DOCX, XLSX, CSV, TXT, MD).',
    "Set each file's <b>Scope</b> to a specific application or leave it Portfolio-wide, and add a description and tags.",
    'Filter the list by filename search, scope, or tag to find documents quickly.',
    'Use the row actions to <b>download</b>, <b>edit</b> (scope/description/tags), or <b>delete</b> a file.',
    'On <b>Scoring Definition &rarr; Supporting documents</b>, attach any of these documents to an initiative&rsquo;s scoring model.',
  ],
  fits: 'ERP Consolidation &rarr; Knowledge Library',
  firstTime:
    'Upload a few contracts or usage reports before launching a run so the agents have context to work from.',
  sections: [
    {
      type: 'defs',
      label: 'Scope',
      items: [
        { term: 'Portfolio', color: '#0d47a1', def: 'Applies to the whole portfolio; every analysis run can reference it.' },
        { term: 'Application', color: '#202124', def: 'Scoped to one application; child apps also inherit documents from their parent.' },
        { term: 'inherited', color: '#0d47a1', def: "Shown when a file is scoped to the selected app's parent and surfaced by inheritance." },
      ],
    },
  ],
};

const TITLE = 'Knowledge Library · Architecture & Process Diagrams';
const SUBTITLE =
  'Architecture diagrams, integration flows, and process maps the analysis will reference.';

const ALLOWED = ['.pdf', '.docx', '.xlsx', '.pptx', '.ppt', '.csv', '.txt', '.md'];

// ─── Helpers — the source's, verbatim ──────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const errorText = (err: unknown, fallback: string): string =>
  err instanceof Error && err.message ? err.message : fallback;

// ─── Preview — the source's renderers ─────────────────────────────────────────

type PreviewKind = 'word' | 'excel' | 'pdf' | 'text';

function previewKind(f?: KnowledgeFileDetail | null): PreviewKind {
  const name = (f?.file_name || '').toLowerCase();
  if (name.endsWith('.docx')) return 'word';
  if (name.endsWith('.xlsx') || name.endsWith('.xls')) return 'excel';
  if (name.endsWith('.pdf')) return 'pdf';
  return 'text'; // pptx/ppt, csv, txt, md — and anything unrecognised
}

function PreviewBody({
  fileId,
  onFile,
}: {
  fileId: string;
  onFile: (file: KnowledgeFileDetail | null) => void;
}) {
  const [file, setFile] = useState<KnowledgeFileDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [rendering, setRendering] = useState(false);
  const [renderErr, setRenderErr] = useState('');
  const [excelHtml, setExcelHtml] = useState<string>('');
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const docxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setErr('');
    erpApi.knowledge
      .get(fileId)
      .then((f) => {
        if (!live) return;
        setFile(f);
        onFile(f);
      })
      .catch((e: unknown) => {
        if (live) setErr(errorText(e, 'Could not load the document.'));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [fileId, onFile]);

  const kind = previewKind(file);

  // Fetch the original bytes and render them, for the formats we can show natively.
  useEffect(() => {
    if (!file || kind === 'text') return;
    let live = true;
    let objectUrl = '';
    setRendering(true);
    setRenderErr('');
    void (async () => {
      try {
        const { blob } = await erpApi.knowledge.download(file.id);
        if (!live) return;
        if (kind === 'pdf') {
          objectUrl = URL.createObjectURL(blob);
          setPdfUrl(objectUrl);
        } else if (kind === 'word') {
          const { renderAsync } = await import('docx-preview');
          if (!live || !docxRef.current) return;
          docxRef.current.innerHTML = '';
          await renderAsync(await blob.arrayBuffer(), docxRef.current, undefined, {
            className: 'docx',
            inWrapper: true,
            breakPages: true,
            ignoreWidth: false,
          });
        } else if (kind === 'excel') {
          const XLSX = await import('xlsx');
          const wb = XLSX.read(await blob.arrayBuffer(), { type: 'array' });
          const parts = wb.SheetNames.map(
            (n) =>
              `<div style="font-weight:700;color:#37474f;margin:12px 0 6px">${n}</div>${XLSX.utils.sheet_to_html(wb.Sheets[n]!)}`,
          );
          if (live) setExcelHtml(parts.join(''));
        }
      } catch (e: unknown) {
        if (live) setRenderErr(errorText(e, 'Could not render this document — use Original to open it.'));
      } finally {
        if (live) setRendering(false);
      }
    })();
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, kind]);

  const text = file?.extracted_text || '';
  const ok = file?.text_extraction_status === 'ok' && text.trim().length > 0;
  const isPpt = /\.pptx?$/i.test(file?.file_name || '');

  return (
    <div className={`kl-preview${kind === 'word' ? ' kl-preview--word' : ''}`}>
      {file && (
        <div className="kl-preview-meta">
          {`${Math.round((file.size_bytes || 0) / 1024).toLocaleString()} KB`}
          {kind === 'text' && ok && ` · ${text.length.toLocaleString()} characters of text the AI reads`}
          {file.uploaded_at && ` · uploaded ${new Date(file.uploaded_at).toLocaleString()}`}
        </div>
      )}
      {err && <div className="kl-preview-err">{err}</div>}
      {loading && <div className="kl-preview-wait">Loading…</div>}
      {rendering && <div className="kl-preview-wait">Rendering document…</div>}
      {renderErr && <div className="kl-note kl-note--warn">{renderErr}</div>}

      {/* Word — real docx layout, paginated */}
      {!loading && kind === 'word' && <div ref={docxRef} />}

      {/* Excel — sheets rendered as tables */}
      {!loading && kind === 'excel' && excelHtml && (
        <div className="kl-preview-excel" dangerouslySetInnerHTML={{ __html: excelHtml }} />
      )}

      {/* PDF — the browser's own viewer */}
      {!loading && kind === 'pdf' && pdfUrl && (
        <iframe title="Document preview" src={pdfUrl} className="kl-preview-pdf" />
      )}

      {/* PowerPoint (and anything else) — the text the AI reads */}
      {!loading && kind === 'text' && file && (
        <>
          {isPpt && (
            <div className="kl-note kl-note--info">
              <strong>Slide preview is not available at the moment.</strong> This feature will be
              available soon.
            </div>
          )}
          {!ok && (
            <div className="kl-note kl-note--warn">
              <strong>No readable text was extracted from this document.</strong> The AI would see
              nothing here, so it can&apos;t be used for analysis.{' '}
              {file.text_extraction_error ? `Extractor said: ${file.text_extraction_error}. ` : ''}
              This usually means a scanned or image-only file — re-export it as a text-based Word
              or PDF and upload again.
            </div>
          )}
          {ok && (
            <>
              <div className="kl-preview-caption">
                This is the text extracted from the document — exactly what the agents read.
                Formatting, images and layout are not part of it.
              </div>
              <div className="kl-preview-text">{text}</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

type Toast = { type: 'success' | 'error'; message: string } | null;

interface UploadForm {
  files: File[];
  applicationId: string;
  description: string;
  tags: string;
}

interface EditForm {
  file: KnowledgeFile;
  applicationId: string;
  description: string;
  tags: string;
}

export function KnowledgeLibrary() {
  const { setRoot, rootStyle } = useScreenLock();
  const [context, setContext] = useState<ErpContext | null>(null);
  const [files, setFiles] = useState<KnowledgeFile[]>([]);
  const [applications, setApplications] = useState<KnowledgeApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  // Panels
  const [upload, setUpload] = useState<UploadForm | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [edit, setEdit] = useState<EditForm | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [deleteFile, setDeleteFile] = useState<KnowledgeFile | null>(null);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<KnowledgeFileDetail | null>(null);

  // Filters
  const [scopeFilter, setScopeFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState('');
  const [search, setSearch] = useState('');

  const canEdit = canEditCapabilityModel(context);

  /* auto-dismiss toast — `FrameworksList.tsx:214-219`, 3000 ms. */
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 3000);
    return () => window.clearTimeout(t);
  }, [toast]);

  // ── Load data ───────────────────────────────────────────────────────────────
  const loadFiles = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [contextPayload, fetchedFiles, fetchedApps] = await Promise.all([
        erpApi.context(),
        erpApi.knowledge.list(),
        erpApi.knowledge.applications(),
      ]);
      setContext(contextPayload.context);
      setFiles(fetchedFiles);
      setApplications(fetchedApps);
    } catch (err) {
      setLoadError(errorText(err, 'Failed to load'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  // ── Handlers ────────────────────────────────────────────────────────────────
  const openUpload = () => {
    setUploadError(null);
    setUpload({ files: [], applicationId: '', description: '', tags: '' });
  };

  const addFiles = (incoming: File[]) => {
    const extOf = (f: File) => f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
    const valid = incoming.filter((f) => ALLOWED.includes(extOf(f)));
    const invalid = incoming.filter((f) => !ALLOWED.includes(extOf(f)));
    setUploadError(
      invalid.length > 0
        ? `Skipped unsupported file(s): ${invalid.map((f) => f.name).join(', ')}`
        : null,
    );
    setUpload((prev) => {
      if (prev === null) return prev;
      const existing = new Set(prev.files.map((f) => f.name));
      return { ...prev, files: [...prev.files, ...valid.filter((f) => !existing.has(f.name))] };
    });
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const submitUpload = async () => {
    if (upload === null) return;
    if (upload.files.length === 0) {
      setUploadError('Please select at least one file.');
      return;
    }
    setBusy(true);
    setUploadError(null);
    try {
      const fd = new FormData();
      upload.files.forEach((f) => fd.append('files', f));
      if (upload.applicationId) fd.append('applicationId', upload.applicationId);
      fd.append('description', upload.description);
      fd.append('tags', upload.tags);
      const uploaded = await erpApi.knowledge.upload(fd);
      setFiles((prev) => [...uploaded, ...prev]);
      setUpload(null);
      setToast({
        type: 'success',
        message: `${uploaded.length} file${uploaded.length === 1 ? '' : 's'} uploaded`,
      });
    } catch (err) {
      setUploadError(errorText(err, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (file: KnowledgeFile) => {
    setEditError(null);
    setEdit({
      file,
      applicationId: file.application_id || '',
      description: file.description || '',
      tags: (file.tags || []).join(', '),
    });
  };

  const submitEdit = async () => {
    if (edit === null) return;
    setBusy(true);
    setEditError(null);
    try {
      const tagsArray = edit.tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      const updated = await erpApi.knowledge.update(edit.file.id, {
        description: edit.description,
        tags: tagsArray,
        application_id: edit.applicationId === '' ? null : edit.applicationId,
      });
      setFiles((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      setEdit(null);
      setToast({ type: 'success', message: 'File updated' });
    } catch (err) {
      setEditError(errorText(err, 'Save failed'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (deleteFile === null) return;
    const target = deleteFile;
    setBusy(true);
    try {
      await erpApi.knowledge.remove(target.id);
      setFiles((prev) => prev.filter((f) => f.id !== target.id));
      setDeleteFile(null);
      setToast({ type: 'success', message: `${target.file_name} deleted` });
    } catch (err) {
      setToast({ type: 'error', message: errorText(err, 'Delete failed') });
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async (file: { id: string }) => {
    try {
      const { blob, filename } = await erpApi.knowledge.download(file.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setToast({ type: 'error', message: errorText(err, 'Download failed') });
    }
  };

  const onPreviewFile = useCallback((file: KnowledgeFileDetail | null) => setPreviewFile(file), []);

  // ── Filtering — the source's, verbatim ──────────────────────────────────────
  // When a specific (child) application is selected, also surface documents
  // inherited from its parent application.
  const selectedApp = applications.find((a) => a.id === scopeFilter);
  const selectedParentId = selectedApp?.parentApplicationId || null;

  const filteredFiles = files.filter((f) => {
    if (scopeFilter === 'portfolio' && f.application_id !== null && f.application_id !== undefined) {
      return false;
    }
    if (scopeFilter !== 'all' && scopeFilter !== 'portfolio') {
      const matchesScope =
        f.application_id === scopeFilter ||
        (selectedParentId !== null && f.application_id === selectedParentId);
      if (!matchesScope) return false;
    }
    if (tagFilter.trim()) {
      const tq = tagFilter.trim().toLowerCase();
      if (!(f.tags || []).some((t) => t.toLowerCase().includes(tq))) return false;
    }
    if (search.trim()) {
      const sq = search.trim().toLowerCase();
      if (!f.file_name.toLowerCase().includes(sq)) return false;
    }
    return true;
  });

  const allTags = Array.from(new Set(files.flatMap((f) => f.tags || []))).sort();

  const scopeLabel = (f: KnowledgeFile) =>
    f.application_id ? f.application_name || f.application_id : 'Portfolio';

  const scopeOptions = (
    <>
      <option value="">Portfolio-wide</option>
      {applications.map((app) => (
        <option key={app.id} value={app.id}>
          {app.name}
        </option>
      ))}
    </>
  );

  const uploadButton = (
    <button type="button" className="erp-rl-btn erp-rl-btn--primary" onClick={openUpload}>
      <i className="material-icons">upload</i>
      Upload Files
    </button>
  );

  return (
    <ErpScreen>
      <div className="page kl-page" ref={setRoot} style={rootStyle}>
        <HelpPanel help={KnowledgeLibraryHelp} />
        <PageHeader title={TITLE} subtitle={SUBTITLE} actions={canEdit ? uploadButton : null} />

        {/* Filter bar — `KnowledgeLibrary.tsx:809-882`, through `recordList.css`. */}
        <div className="erp-rl-filter-bar">
          <div className="erp-rl-search">
            <i className="material-icons">search</i>
            <input
              id="erp-kl-search"
              type="text"
              className="erp-rl-input"
              placeholder="Search by filename…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <select
            id="erp-kl-scope-filter"
            className="erp-rl-select"
            aria-label="Scope"
            value={scopeFilter}
            onChange={(e) => setScopeFilter(e.target.value)}
          >
            <option value="all">All scopes</option>
            <option value="portfolio">Portfolio-wide</option>
            {applications.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name}
              </option>
            ))}
          </select>

          {allTags.length > 0 ? (
            <select
              id="erp-kl-tag-filter"
              className="erp-rl-select"
              aria-label="Tag"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <option value="">All tags</option>
              {allTags.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          ) : (
            <input
              id="erp-kl-tag-filter"
              type="text"
              className="erp-rl-input kl-tag-input"
              placeholder="Filter by tag…"
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
            />
          )}

          {(search || scopeFilter !== 'all' || tagFilter) && (
            <button
              type="button"
              className="erp-rl-btn erp-rl-btn--ghost"
              onClick={() => {
                setSearch('');
                setScopeFilter('all');
                setTagFilter('');
              }}
            >
              <i className="material-icons">clear</i>
              Clear
            </button>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="erp-rl-loading">
            <i className="material-icons erp-rl-spin">sync</i>
            <p>Loading files…</p>
          </div>
        ) : loadError ? (
          <div className="erp-panel-error">
            <i className="material-icons erp-rl-error-icon">error</i>
            {loadError}
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="erp-rl-empty">
            <div className="erp-rl-empty-icon">
              <i className="material-icons">folder_open</i>
            </div>
            {files.length === 0 ? (
              <>
                <h2 className="erp-rl-empty-title">No files yet</h2>
                <p className="erp-rl-empty-body">Upload your first one to give the agents context.</p>
                {canEdit && <div className="kl-empty-action">{uploadButton}</div>}
              </>
            ) : (
              <>
                <h2 className="erp-rl-empty-title">No matching files</h2>
                <p className="erp-rl-empty-body">Try adjusting your filters.</p>
              </>
            )}
          </div>
        ) : (
          <div className="erp-rl-table-wrapper">
            <table className="erp-rl-table">
              <thead>
                <tr>
                  <th>File name</th>
                  <th>Scope</th>
                  <th>Tags</th>
                  <th>Uploaded by</th>
                  <th>Uploaded at</th>
                  <th>Size</th>
                  <th className="kl-actions-h">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map((f) => (
                  <tr key={f.id}>
                    <td>
                      <span className="erp-rl-name">{f.file_name}</span>
                      {f.description && <span className="erp-rl-desc">{f.description}</span>}
                    </td>
                    <td>
                      <span
                        className={`erp-rl-badge ${f.application_id ? 'erp-rl-badge--neutral' : 'erp-rl-badge--info'}`}
                      >
                        {scopeLabel(f)}
                      </span>
                      {selectedParentId !== null && f.application_id === selectedParentId && (
                        <span
                          className="kl-inherited"
                          title={`Inherited from parent application ${scopeLabel(f)}`}
                        >
                          inherited
                        </span>
                      )}
                    </td>
                    <td>
                      <div className="erp-rl-tags">
                        {(f.tags || []).length === 0 ? (
                          <span className="erp-rl-none">—</span>
                        ) : (
                          (f.tags || []).map((tag) => (
                            <span key={tag} className="erp-rl-tag">
                              {tag}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="erp-rl-sec">{f.uploaded_by_name || '—'}</td>
                    <td className="erp-rl-sec erp-rl-nowrap">{formatDate(f.uploaded_at)}</td>
                    <td className="erp-rl-sec erp-rl-nowrap">{formatBytes(f.size_bytes)}</td>
                    <td>
                      <div className="erp-rl-actions">
                        <button
                          type="button"
                          className="erp-rl-glyph erp-rl-glyph--view"
                          title="Preview the text the AI reads from this document"
                          onClick={() => setPreviewId(f.id)}
                        >
                          👁️
                        </button>
                        <button
                          type="button"
                          className="erp-rl-glyph"
                          title="Download the original file"
                          onClick={() => void handleDownload(f)}
                        >
                          ⬇️
                        </button>
                        {canEdit && (
                          <>
                            <button
                              type="button"
                              className="erp-rl-glyph"
                              title="Edit"
                              onClick={() => openEdit(f)}
                            >
                              ✏️
                            </button>
                            <button
                              type="button"
                              className="erp-rl-glyph erp-rl-glyph--delete"
                              title="Delete"
                              onClick={() => setDeleteFile(f)}
                            >
                              🗑️
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ── Upload — the source's modal, as the module's single-width panel ──────────── */}
        {upload !== null && (
          <FormPanel
            open
            title="Upload Files"
            subtitle={TITLE}
            onRequestClose={() => setUpload(null)}
            onSave={() => void submitUpload()}
            busy={busy}
            saveLabel={`Upload ${upload.files.length > 0 ? `(${upload.files.length})` : ''}`}
            busyLabel="Uploading…"
            dirty={upload.files.length > 0 || upload.description !== '' || upload.tags !== ''}
            discardMessage="Discard this upload?"
          >
            <div
              className={`kl-dropzone${dragOver ? ' kl-dropzone--over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
            >
              <i className="material-icons kl-dropzone-icon">cloud_upload</i>
              <p className="kl-dropzone-title">Drag files here or click to browse</p>
              <p className="kl-dropzone-hint">
                Supported: PDF, DOCX, XLSX, PPTX, PPT, CSV, TXT, MD — multiple files allowed
              </p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.pptx,.ppt,.csv,.txt,.md"
                hidden
                onChange={(e) => {
                  if (e.target.files) addFiles(Array.from(e.target.files));
                  e.target.value = '';
                }}
              />
            </div>

            {upload.files.length > 0 && (
              <ul className="kl-file-list">
                {upload.files.map((f) => (
                  <li key={f.name}>
                    <i className="material-icons">insert_drive_file</i>
                    <span className="kl-file-name">{f.name}</span>
                    <span className="kl-file-size">{formatBytes(f.size)}</span>
                    <button
                      type="button"
                      className="erp-rl-btn erp-rl-btn--ghost kl-file-remove"
                      aria-label={`Remove ${f.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setUpload((prev) =>
                          prev === null
                            ? prev
                            : { ...prev, files: prev.files.filter((x) => x.name !== f.name) },
                        );
                      }}
                    >
                      <i className="material-icons">close</i>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            <div className="form-group">
              <label htmlFor="erp-kl-scope">Scope</label>
              <select
                id="erp-kl-scope"
                value={upload.applicationId}
                onChange={(e) => setUpload({ ...upload, applicationId: e.target.value })}
              >
                {scopeOptions}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="erp-kl-desc">Description</label>
              <textarea
                id="erp-kl-desc"
                rows={2}
                placeholder="Brief note about these files…"
                value={upload.description}
                onChange={(e) => setUpload({ ...upload, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-kl-tags">Tags</label>
              <input
                id="erp-kl-tags"
                type="text"
                placeholder="contract, usage-report, vendor-quote"
                value={upload.tags}
                onChange={(e) => setUpload({ ...upload, tags: e.target.value })}
              />
              <span className="kl-form-hint">Comma-separated</span>
            </div>
            {uploadError && (
              <p className="erp-panel-error" role="alert">
                {uploadError}
              </p>
            )}
          </FormPanel>
        )}

        {/* ── Edit — scope, description, tags ────────────────────────────────────────── */}
        {edit !== null && (
          <FormPanel
            open
            title="Edit File"
            subtitle={edit.file.file_name}
            onRequestClose={() => setEdit(null)}
            onSave={() => void submitEdit()}
            busy={busy}
            saveLabel="Save"
            busyLabel="Saving…"
            dirty={
              edit.applicationId !== (edit.file.application_id || '') ||
              edit.description !== (edit.file.description || '') ||
              edit.tags !== (edit.file.tags || []).join(', ')
            }
            discardMessage="Discard these changes?"
          >
            <div className="form-group">
              <label htmlFor="erp-kl-edit-scope">Scope</label>
              <select
                id="erp-kl-edit-scope"
                value={edit.applicationId}
                onChange={(e) => setEdit({ ...edit, applicationId: e.target.value })}
              >
                {scopeOptions}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="erp-kl-edit-desc">Description</label>
              <textarea
                id="erp-kl-edit-desc"
                rows={2}
                value={edit.description}
                onChange={(e) => setEdit({ ...edit, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="erp-kl-edit-tags">Tags</label>
              <input
                id="erp-kl-edit-tags"
                type="text"
                placeholder="contract, usage-report"
                value={edit.tags}
                onChange={(e) => setEdit({ ...edit, tags: e.target.value })}
              />
              <span className="kl-form-hint">Comma-separated</span>
            </div>
            {editError && (
              <p className="erp-panel-error" role="alert">
                {editError}
              </p>
            )}
          </FormPanel>
        )}

        {/* ── Preview — the double-width panel; the source's is min(900px, 96vw) ────────── */}
        {previewId !== null && (
          <FormPanel
            open
            width="double"
            title={previewFile?.file_name || 'Document'}
            onRequestClose={() => {
              setPreviewId(null);
              setPreviewFile(null);
            }}
            onSave={() => (previewFile ? void handleDownload(previewFile) : undefined)}
            saveLabel="⬇️ Original"
            showSaveIcon={false}
            cancelLabel="Close"
          >
            <PreviewBody fileId={previewId} onFile={onPreviewFile} />
          </FormPanel>
        )}

        {deleteFile && (
          <ConfirmDialog
            open
            title="Delete File"
            message={
              `Are you sure you want to delete ${deleteFile.file_name}? This action cannot be ` +
              "undone. It is also removed from APR's Knowledge Library and detached from every " +
              'scoring definition that uses it.'
            }
            confirmLabel={busy ? 'Deleting…' : 'Delete'}
            onConfirm={() => void confirmDelete()}
            onCancel={() => setDeleteFile(null)}
          />
        )}

        {toast && (
          <div className={`kl-toast kl-toast-${toast.type}`} role="status">
            {toast.type === 'success' ? '✓ ' : '⚠ '}
            {toast.message}
          </div>
        )}
      </div>
    </ErpScreen>
  );
}
